import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  MapPin, 
  Briefcase, 
  Clock,
  Building2,
  Loader2,
  ArrowLeft,
  CheckCircle,
  FileText,
  Globe,
  Sparkles,
  ShieldOff,
  ShieldCheck
} from 'lucide-react';
import { getJob, createApplication, generateCoverLetter, blockJob, unblockJob, getBlockedJobs } from '../services/api';
import { toast } from 'sonner';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    fetchJob();
    checkBlocked();
  }, [jobId]);

  const checkBlocked = async () => {
    try {
      const res = await getBlockedJobs();
      setIsBlocked(res.data.some(b => b.job_id === jobId));
    } catch {}
  };

  const handleToggleBlock = async () => {
    setBlocking(true);
    try {
      if (isBlocked) {
        await unblockJob(jobId);
        setIsBlocked(false);
        toast.success('Blockierung aufgehoben');
      } else {
        await blockJob(jobId);
        setIsBlocked(true);
        toast.success('Stelle blockiert');
      }
    } catch (error) {
      toast.error('Fehler');
    } finally {
      setBlocking(false);
    }
  };

  const fetchJob = async () => {
    try {
      const res = await getJob(jobId);
      setJob(res.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Job nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCoverLetter = async () => {
    setGeneratingLetter(true);
    try {
      const res = await generateCoverLetter(jobId, 'de');
      setCoverLetter(res.data.cover_letter);
      toast.success('Anschreiben generiert');
    } catch (error) {
      toast.error('Fehler beim Generieren');
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await createApplication({ job_id: jobId, cover_letter: coverLetter });
      toast.success('Bewerbung erfolgreich gesendet!');
      setShowApplyDialog(false);
      navigate('/applications');
    } catch (error) {
      if (error.response?.data?.detail === 'Sie haben sich bereits beworben') {
        toast.error('Sie haben sich bereits auf diese Stelle beworben');
      } else {
        toast.error('Fehler beim Bewerben');
      }
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Job nicht gefunden</p>
            <Button onClick={() => navigate('/jobs')} className="mt-4 rounded-full">
              Zurück zu Jobs
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="job-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        {/* Job Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-heading font-bold text-foreground">{job.title}</h1>
                <p className="text-lg text-muted-foreground">{job.company_name || 'Unternehmen'}</p>
                
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {job.job_type}
                  </span>
                  {job.salary_range && (
                    <span className="flex items-center gap-1">
                      {job.salary_range}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(job.created_at).toLocaleDateString('de-DE')}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {job.remote_option && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      Remote möglich
                    </span>
                  )}
                  {job.experience_level && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {job.experience_level}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {user?.user_type === 'applicant' && (
              <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-3">
                <Button 
                  size="lg" 
                  className="rounded-full flex-1 sm:flex-none"
                  onClick={() => setShowApplyDialog(true)}
                  data-testid="apply-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Jetzt bewerben
                </Button>
                <Button
                  size="lg"
                  variant={isBlocked ? "default" : "outline"}
                  className={`rounded-full ${isBlocked ? 'bg-red-500 hover:bg-red-600 text-white' : 'text-muted-foreground hover:text-red-500 hover:border-red-200'}`}
                  onClick={handleToggleBlock}
                  disabled={blocking}
                  data-testid="block-job-btn"
                >
                  {blocking ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : isBlocked ? (
                    <ShieldCheck className="w-5 h-5 mr-2" />
                  ) : (
                    <ShieldOff className="w-5 h-5 mr-2" />
                  )}
                  {isBlocked ? 'Entblockieren' : 'Blockieren'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Job Description */}
        <Card>
          <CardHeader>
            <CardTitle>Stellenbeschreibung</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{job.description}</p>
          </CardContent>
        </Card>

        {/* Tasks */}
        {job.tasks && job.tasks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Ihre Aufgaben</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {job.tasks.map((task, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{task}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Anforderungen</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {job.requirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Benefits */}
        {job.benefits && job.benefits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Benefits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map((benefit, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
                  >
                    {benefit}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Languages */}
        {job.languages_required && job.languages_required.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Sprachkenntnisse
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {job.languages_required.map((lang, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded-full text-sm"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Apply Dialog */}
        <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Bewerbung für: {job.title}</DialogTitle>
              <DialogDescription>
                Fügen Sie ein Anschreiben hinzu oder lassen Sie es von der KI generieren
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 my-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Anschreiben</label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleGenerateCoverLetter}
                  disabled={generatingLetter}
                  data-testid="generate-cover-letter-btn"
                >
                  {generatingLetter ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Mit KI generieren
                </Button>
              </div>
              <Textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Schreiben Sie Ihr Anschreiben oder nutzen Sie die KI-Generierung..."
                rows={10}
                data-testid="cover-letter-textarea"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleApply} 
                disabled={applying}
                className="rounded-full"
                data-testid="submit-application-btn"
              >
                {applying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Bewerbung absenden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default JobDetail;
